"use client";

import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { PolicyLayout } from "../../_components/PolicyLayout";
import { useTranslations } from "next-intl";
import {
  IoFingerPrint,
  IoAnalytics,
  IoShieldCheckmark,
  IoShareSocial,
  IoGlobe,
  IoTime,
  IoLockClosed,
  IoPeople,
  IoDocumentText,
  IoShieldOutline,
  IoRefresh,
  IoMail,
} from "react-icons/io5";
import { HELP_CENTER_IDS } from "../../_constants/help-center.ids";
import {
  PolicyIntroCard,
  PolicyList,
  PolicySectionCard,
  PolicySubheading,
  PolicyText,
} from "../../_components/policy/PolicyContentPrimitives";

export function PrivacyPolicyContent() {
  const t = useTranslations("landing.policies");

  return (
    <PolicyLayout
      title={t("privacy.title")}
      lastUpdated={t("privacy.last_updated")}
    >
      <PolicyIntroCard id={HELP_CENTER_IDS.privacy.intro}>
        {t("privacy.intro")}
      </PolicyIntroCard>

      <Flex id={HELP_CENTER_IDS.privacy.sections} direction="column" gap={3}>
        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(1)}
          icon={IoFingerPrint}
          number={1}
          sectionTitle={t("privacy.info_collect.heading")}
        >
          <PolicyText>{t("privacy.info_collect.content")}</PolicyText>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Box bg="neutral.50" borderRadius="lg" p={4}>
              <PolicySubheading>
                {t("privacy.info_collect.personal.heading")}
              </PolicySubheading>
              <PolicyList
                items={[
                  t("privacy.info_collect.personal.item1"),
                  t("privacy.info_collect.personal.item2"),
                  t("privacy.info_collect.personal.item3"),
                  t("privacy.info_collect.personal.item4"),
                ]}
              />
            </Box>
            <Box bg="neutral.50" borderRadius="lg" p={4}>
              <PolicySubheading>
                {t("privacy.info_collect.auto.heading")}
              </PolicySubheading>
              <PolicyList
                items={[
                  t("privacy.info_collect.auto.item1"),
                  t("privacy.info_collect.auto.item2"),
                  t("privacy.info_collect.auto.item3"),
                ]}
              />
            </Box>
          </SimpleGrid>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(2)}
          icon={IoAnalytics}
          number={2}
          sectionTitle={t("privacy.use_data.heading")}
        >
          <PolicyText>{t("privacy.use_data.content")}</PolicyText>
          <PolicyList
            items={[
              t("privacy.use_data.item1"),
              t("privacy.use_data.item2"),
              t("privacy.use_data.item3"),
              t("privacy.use_data.item4"),
              t("privacy.use_data.item5"),
            ]}
          />
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(3)}
          icon={IoDocumentText}
          number={3}
          sectionTitle={t("privacy.legal_basis.heading")}
        >
          <PolicyText>{t("privacy.legal_basis.content")}</PolicyText>
          <PolicyList
            items={[
              t("privacy.legal_basis.item1"),
              t("privacy.legal_basis.item2"),
              t("privacy.legal_basis.item3"),
              t("privacy.legal_basis.item4"),
            ]}
          />
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(4)}
          icon={IoShareSocial}
          number={4}
          sectionTitle={t("privacy.sharing.heading")}
        >
          <PolicyText>{t("privacy.sharing.content")}</PolicyText>
          <PolicyList
            items={[
              t("privacy.sharing.item1"),
              t("privacy.sharing.item2"),
              t("privacy.sharing.item3"),
            ]}
          />
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(5)}
          icon={IoGlobe}
          number={5}
          sectionTitle={t("privacy.international.heading")}
        >
          <PolicyText>{t("privacy.international.content")}</PolicyText>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(6)}
          icon={IoTime}
          number={6}
          sectionTitle={t("privacy.retention.heading")}
        >
          <PolicyText>{t("privacy.retention.content")}</PolicyText>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(7)}
          icon={IoLockClosed}
          number={7}
          sectionTitle={t("privacy.security.heading")}
        >
          <PolicyText>{t("privacy.security.content")}</PolicyText>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(8)}
          icon={IoPeople}
          number={8}
          sectionTitle={t("privacy.rights.heading")}
        >
          <PolicyText>{t("privacy.rights.content")}</PolicyText>

          <SimpleGrid columns={{ base: 1, md: 1 }} gap={4}>
            <Box bg="neutral.50" borderRadius="lg" p={4}>
              <PolicySubheading>
                {t("privacy.rights.gdpr.heading")}
              </PolicySubheading>
              <PolicyText>{t("privacy.rights.gdpr.content")}</PolicyText>
              <PolicyList
                items={[
                  t("privacy.rights.gdpr.item1"),
                  t("privacy.rights.gdpr.item2"),
                  t("privacy.rights.gdpr.item3"),
                  t("privacy.rights.gdpr.item4"),
                  t("privacy.rights.gdpr.item5"),
                  t("privacy.rights.gdpr.item6"),
                ]}
              />
            </Box>
            <Flex direction="column" gap={4}>
              <Box bg="neutral.50" borderRadius="lg" p={4}>
                <PolicySubheading>
                  {t("privacy.rights.vietnam.heading")}
                </PolicySubheading>
                <PolicyText>{t("privacy.rights.vietnam.content")}</PolicyText>
              </Box>
              <Box bg="neutral.50" borderRadius="lg" p={4}>
                <PolicySubheading>
                  {t("privacy.rights.singapore.heading")}
                </PolicySubheading>
                <PolicyText>{t("privacy.rights.singapore.content")}</PolicyText>
              </Box>
              <Box bg="neutral.50" borderRadius="lg" p={4}>
                <PolicySubheading>
                  {t("privacy.rights.australia.heading")}
                </PolicySubheading>
                <PolicyText>{t("privacy.rights.australia.content")}</PolicyText>
              </Box>
              <Box bg="neutral.50" borderRadius="lg" p={4}>
                <PolicySubheading>
                  {t("privacy.rights.us.heading")}
                </PolicySubheading>
                <PolicyText>{t("privacy.rights.us.content")}</PolicyText>
              </Box>
            </Flex>
          </SimpleGrid>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(9)}
          icon={IoShieldCheckmark}
          number={9}
          sectionTitle={t("privacy.cookies.heading")}
        >
          <PolicyText>{t("privacy.cookies.content")}</PolicyText>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(10)}
          icon={IoShieldOutline}
          number={10}
          sectionTitle={t("privacy.children.heading")}
        >
          <PolicyText>{t("privacy.children.content")}</PolicyText>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(11)}
          icon={IoRefresh}
          number={11}
          sectionTitle={t("privacy.changes.heading")}
        >
          <PolicyText>{t("privacy.changes.content")}</PolicyText>
        </PolicySectionCard>

        <PolicySectionCard
          sectionId={HELP_CENTER_IDS.privacy.section(12)}
          icon={IoMail}
          number={12}
          sectionTitle={t("privacy.contact.heading")}
        >
          <PolicyText>{t("privacy.contact.content")}</PolicyText>
        </PolicySectionCard>
      </Flex>
    </PolicyLayout>
  );
}
